import { lazy } from 'react'
import DashboardIcon from '@mui/icons-material/Dashboard'
import {
  AirlineSeatReclineExtra,
  Assessment,
  Business,
  CalendarMonth,
  DriveEta,
  EmojiTransportation,
  ForkRight,
  Label,
  LocalShipping,
  Map,
  People,
  Settings,
  ShapeLine,
  Notifications as NotificationIcon,
  NewReleases,
  PhoneMissed,
  Phone,
  CheckBox,
  NoCrash,
  FactCheck,
  Error,
  PhoneDisabled,
  CompareArrows,
  CarCrash,
  Cancel,
  Radar,
  TransferWithinAStation,
  Email,
  AssignmentInd,
  Garage,
  AccountBalance,
  Policy,
  Balance,
  SupportAgent,
  ContactPage,
} from '@mui/icons-material'
import { PAGE_URLS } from '../helpers/helper'
import { MenuJobsStatus, TMenuItems } from '../types/layoutTypes'

const ChangePassword = lazy(() => import('../../pages/auth/ChangePassword'))
const Dashboard = lazy(() => import('../../pages/dashboard/Dashboard'))
const RoutesList = lazy(() => import('../../pages/routes/RoutesMain'))
const JobsMain = lazy(() => import('../../pages/jobs/JobsMain'))
const DepotsList = lazy(() => import('../../pages/depots/DepotsList'))
const OrganisationsList = lazy(
  () => import('../../pages/organisations/OrganisationsList')
)
const OrganisationsEdit = lazy(
  () => import('../../pages/organisations/OrganisationsEdit')
)
const JobsAdd = lazy(() => import('../../pages/jobs/JobsAdd'))
const DepotsEdit = lazy(() => import('../../pages/depots/DepotsEdit'))
const DepotsAdd = lazy(() => import('../../pages/depots/DepotsAdd'))
const AdminDriversList = lazy(
  () => import('../../pages/drivers/Admin/AdminDriversList')
)
const AdminDriversAdd = lazy(
  () => import('../../pages/drivers/Admin/AdminDriversAdd')
)
const AdminDriversEdit = lazy(
  () => import('../../pages/drivers/Admin/AdminDriversEdit')
)
const AdminTransportersList = lazy(
  () => import('../../pages/transporters/Admin/AdminTransportersList')
)
const AdminTransportersAdd = lazy(
  () => import('../../pages/transporters/Admin/AdminTransportersAdd')
)
const AdminTransportersEdit = lazy(
  () => import('../../pages/transporters/Admin/AdminTransportersEdit')
)
const Unauthorized = lazy(() => import('../../pages/auth/Unauthorized'))
const UsersList = lazy(() => import('../../pages/users/UsersList'))
const UsersAdd = lazy(() => import('../../pages/users/UsersAdd'))
const UsersEdit = lazy(() => import('../../pages/users/UsersEdit'))
const JobsReport = lazy(() => import('../../pages/reports/JobsReport'))
const RoutesReport = lazy(() => import('../../pages/reports/RoutesReport'))
const JobsEditFullPage = lazy(() => import('../../pages/jobs/JobsEditFullPage'))
const RoutesEditFullPage = lazy(
  () => import('../../pages/routes/RoutesEditFullPage')
)
const AdminTagsMain = lazy(() => import('../../pages/tags/AdminTagsMain'))
const AdminTagsAdd = lazy(() => import('../../pages/tags/AdminTagsAdd'))
const AdminTagsEdit = lazy(() => import('../../pages/tags/AdminTagsEdit'))
const RoutesPlanPage = lazy(
  () => import('../../pages/routesPlan/RoutesPlanPage')
)
const MapViewPage = lazy(() => import('../../pages/mapView/MapViewPage'))
const DriverDashboard = lazy(
  () => import('../../pages/driverView/DriverDashboard')
)
const DriverRoute = lazy(() => import('../../pages/driverView/DriverRoute'))
const DriverJob = lazy(() => import('../../pages/driverView/DriverJob'))
const RouteCalendar = lazy(
  () => import('../../pages/routeCalendar/RouteCalendar')
)
const NotificationMain = lazy(
  () => import('../../pages/notification/NotificationMain')
)
const NoCompanyOption = lazy(() => import('../../pages/auth/NoCompanyOption'))
const OrganisationsAddCollector = lazy(
  () => import('../../pages/organisations/OrganisationsAddCollector')
)
const Logout = lazy(() => import('../../pages/auth/login/Logout'))
const ZonesList = lazy(() => import('../../pages/zones/ZonesList'))
const ZoneEdit = lazy(() => import('../../pages/zones/ZoneEdit'))
const ZoneAdd = lazy(() => import('../../pages/zones/ZoneAdd'))
const EmailsReport = lazy(() => import('../../pages/reports/EmailsReport'))
const VersionList = lazy(() => import('../../pages/version/VersionList'))
const VersionAdd = lazy(() => import('../../pages/version/VersionAdd'))
const VersionEdit = lazy(() => import('../../pages/version/VersionEdit'))
const SupportRequestsMain = lazy(
  () => import('../../pages/support/SupportRequestsMain')
)
const SupportRequestView = lazy(
  () => import('../../pages/support/SupportRequestView')
)
const CustomerDatabaseAdd = lazy(
  () => import('../../pages/customerDatabase/CustomerDatabaseAdd')
)
const CustomerDatabaseEdit = lazy(
  () => import('../../pages/customerDatabase/CustomerDatabaseEdit')
)
const CustomerDatabaseMain = lazy(
  () => import('../../pages/customerDatabase/CustomerDatabaseList')
)

const loggedInRoutes = [
  {
    path: PAGE_URLS.DASHBOARD,
    element: <Dashboard />,
    requiredCompanyOption: ['MENU_DASHBOARD'],
  },
  {
    path: PAGE_URLS.NOTIFICATION,
    element: <NotificationMain />,
    requiredCompanyOption: ['MENU_NOTIFICATION'],
  },
  {
    path: PAGE_URLS.ROUTE_CALENDAR,
    element: <RouteCalendar />,
    requiredCompanyOption: ['MENU_ROUTE_CALENDAR'],
  },
  { path: PAGE_URLS.SUPPORT, element: <SupportRequestsMain /> },
  {
    path: `${PAGE_URLS.SUPPORT}/:supportRequestId`,
    element: <SupportRequestView />,
  },
  {
    path: PAGE_URLS.JOBS,
    element: <JobsMain />,
    requiredCompanyOption: ['MENU_JOBS'],
  },
  {
    path: `${PAGE_URLS.JOBS}/add`,
    element: <JobsAdd />,
    requiredCompanyOption: ['MENU_JOBS'],
  },
  {
    path: `${PAGE_URLS.JOBS}/:jobId`,
    element: <JobsEditFullPage />,
    requiredCompanyOption: ['MENU_JOBS'],
  },
  {
    path: PAGE_URLS.ROUTES,
    element: <RoutesList />,
    requiredCompanyOption: ['MENU_ROUTES'],
  },
  {
    path: `${PAGE_URLS.ROUTES}/:routeId`,
    element: <RoutesEditFullPage />,
    requiredCompanyOption: ['MENU_ROUTES'],
  },
  {
    path: PAGE_URLS.MAP_VIEW,
    element: <MapViewPage />,
    requiredCompanyOption: ['MENU_MAP_VIEW'],
  },
  {
    path: PAGE_URLS.ROUTES_PLAN,
    element: <RoutesPlanPage />,
    requiredCompanyOption: ['MENU_ROUTES_PLAN'],
  },
  {
    path: PAGE_URLS.JOBS_REPORT,
    element: <JobsReport />,
    requiredCompanyOption: ['MENU_REPORTS'],
  },
  {
    path: PAGE_URLS.ROUTES_REPORT,
    element: <RoutesReport />,
    requiredCompanyOption: ['MENU_REPORTS'],
  },
  {
    path: PAGE_URLS.EMAILS_REPORT,
    element: <EmailsReport />,
    requiredCompanyOption: ['MENU_REPORTS'],
  },
  { path: PAGE_URLS.CHANGE_PASSWORD, element: <ChangePassword /> },
  { path: PAGE_URLS.UNAUTHORIZED, element: <Unauthorized /> },
  { path: PAGE_URLS.NO_COMPANY_OPTION, element: <NoCompanyOption /> },
  { path: PAGE_URLS.LOGOUT, element: <Logout /> },
  {
    path: PAGE_URLS.SETTINGS_VERSION,
    element: <VersionList />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
]

const loggedInDriverRoutes = [
  { path: PAGE_URLS.DRIVER_DASHBOARD, element: <DriverDashboard /> },
  { path: `${PAGE_URLS.DRIVER_ROUTES}/:routeId`, element: <DriverRoute /> },
  { path: `${PAGE_URLS.DRIVER_JOBS}/:jobId`, element: <DriverJob /> },
]

const adminsRoutes = [
  {
    path: PAGE_URLS.SETTINGS_ORGANISATIONS,
    element: <OrganisationsList />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_ORGANISATIONS}/add`,
    element: <OrganisationsAddCollector />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_ORGANISATIONS}/:organisationId`,
    element: <OrganisationsEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: PAGE_URLS.SETTINGS_CUSTOMERS,
    element: <CustomerDatabaseMain />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_CUSTOMERS}/add`,
    element: <CustomerDatabaseAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_CUSTOMERS}/:customerId`,
    element: <CustomerDatabaseEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: PAGE_URLS.SETTINGS_ZONES,
    element: <ZonesList />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_ZONES}/add`,
    element: <ZoneAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_ZONES}/:zoneId`,
    element: <ZoneEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC'],
  },
  {
    path: PAGE_URLS.SETTINGS_DEPOTS,
    element: <DepotsList />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_DEPOTS}/add`,
    element: <DepotsAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_DEPOTS}/:depotId`,
    element: <DepotsEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: PAGE_URLS.SETTINGS_DRIVERS,
    element: <AdminDriversList />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_DRIVERS}/add`,
    element: <AdminDriversAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_DRIVERS}/:driverId`,
    element: <AdminDriversEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: PAGE_URLS.SETTINGS_TRANSPORTERS,
    element: <AdminTransportersList />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_TRANSPORTERS}/add`,
    element: <AdminTransportersAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_TRANSPORTERS}/:transporterId`,
    element: <AdminTransportersEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_FULL'],
  },
  {
    path: PAGE_URLS.SETTINGS_USERS,
    element: <UsersList />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_USERS}/add`,
    element: <UsersAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_USERS}/:userId`,
    element: <UsersEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: PAGE_URLS.SETTINGS_TAGS,
    element: <AdminTagsMain />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_TAGS}/add`,
    element: <AdminTagsAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_TAGS}/:tagId`,
    element: <AdminTagsEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_VERSION}/add`,
    element: <VersionAdd />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
  {
    path: `${PAGE_URLS.SETTINGS_VERSION}/:versionId`,
    element: <VersionEdit />,
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
  },
]

const MENU_ITEMS: TMenuItems[] = [
  {
    label: 'Dashboard',
    icon: <DashboardIcon />,
    goToUrl: PAGE_URLS.DASHBOARD,
    requiredCompanyOption: ['MENU_DASHBOARD'],
  },
  {
    label: 'Notifications',
    icon: <NotificationIcon />,
    goToUrl: PAGE_URLS.NOTIFICATION,
    requiredCompanyOption: ['MENU_NOTIFICATION'],
  },
  {
    label: 'Support',
    icon: <SupportAgent />,
    goToUrl: PAGE_URLS.SUPPORT,
    requiredCompanyOption: [],
  },
  {
    label: 'Jobs',
    icon: <DriveEta />,
    goToUrl: PAGE_URLS.JOBS,
    requiredCompanyOption: ['MENU_JOBS'],
  },
  {
    label: 'Routes',
    icon: <ForkRight />,
    goToUrl: PAGE_URLS.ROUTES,
    requiredCompanyOption: ['MENU_ROUTES'],
  },
  {
    label: 'Route Calendar',
    icon: <CalendarMonth />,
    goToUrl: PAGE_URLS.ROUTE_CALENDAR,
    requiredCompanyOption: ['MENU_ROUTE_CALENDAR'],
  },
  {
    label: 'Map View',
    icon: <Map />,
    goToUrl: PAGE_URLS.MAP_VIEW,
    requiredCompanyOption: ['MENU_MAP_VIEW'],
  },
  {
    label: 'Routes Plan',
    icon: <ShapeLine />,
    goToUrl: PAGE_URLS.ROUTES_PLAN,
    requiredCompanyOption: ['MENU_ROUTES_PLAN'],
  },
  {
    label: 'Reports',
    icon: <Assessment />,
    goToUrl: '',
    requiredCompanyOption: ['MENU_REPORTS'],
    subMenus: [
      {
        label: 'Jobs',
        icon: <DriveEta />,
        goToUrl: PAGE_URLS.JOBS_REPORT,
      },
      {
        label: 'Routes',
        icon: <ForkRight />,
        goToUrl: PAGE_URLS.ROUTES_REPORT,
      },
      {
        label: 'Emails',
        icon: <Email />,
        goToUrl: PAGE_URLS.EMAILS_REPORT,
      },
    ],
  },
  {
    label: 'Settings',
    icon: <Settings />,
    goToUrl: '',
    requiredRoles: ['ROLE_ADMIN', 'ROLE_ZONE_ADMIN'],
    requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
    subMenus: [
      {
        label: 'Organisations',
        icon: <Business />,
        goToUrl: PAGE_URLS.SETTINGS_ORGANISATIONS,
        requiredCompanyOption: ['MENU_SETTINGS_FULL'],
      },
      {
        label: 'Zones',
        icon: <Radar />,
        goToUrl: PAGE_URLS.SETTINGS_ZONES,
        requiredCompanyOption: ['MENU_SETTINGS_BASIC'],
      },
      {
        label: 'Depots',
        icon: <EmojiTransportation />,
        goToUrl: PAGE_URLS.SETTINGS_DEPOTS,
        requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
      },
      {
        label: 'Drivers',
        icon: <AirlineSeatReclineExtra />,
        goToUrl: PAGE_URLS.SETTINGS_DRIVERS,
        requiredCompanyOption: ['MENU_SETTINGS_FULL'],
      },
      {
        label: 'Transporters',
        icon: <LocalShipping />,
        goToUrl: PAGE_URLS.SETTINGS_TRANSPORTERS,
        requiredCompanyOption: ['MENU_SETTINGS_FULL'],
      },
      {
        label: 'Users',
        icon: <People />,
        goToUrl: PAGE_URLS.SETTINGS_USERS,
        requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
      },
      {
        label: 'Customers',
        icon: <ContactPage />,
        goToUrl: PAGE_URLS.SETTINGS_CUSTOMERS,
        requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
      },
      {
        label: 'Tags',
        icon: <Label />,
        goToUrl: PAGE_URLS.SETTINGS_TAGS,
        requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
      },
      {
        label: 'Versions',
        icon: <NewReleases />,
        goToUrl: PAGE_URLS.SETTINGS_VERSION,
        requiredCompanyOption: ['MENU_SETTINGS_BASIC', 'MENU_SETTINGS_FULL'],
      },
    ],
  },
]

const MENU_JOBS_STATUS: MenuJobsStatus[] = [
  {
    label: 'New Jobs',
    icon: <NewReleases />,
    goToUrl: `${PAGE_URLS.JOBS}?status=New Jobs`,
    counterLabel: 'newJobs',
  },
  {
    label: 'Arranging Collection',
    icon: <LocalShipping />,
    goToUrl: `${PAGE_URLS.JOBS}?status=Arranging Collection`,
    counterLabel: 'arrangingCollection',
    subMenus: [
      {
        label: 'No Answer',
        icon: <PhoneMissed fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Arranging No Answer`,
        counterLabel: 'noAnswer',
      },
      {
        label: 'Contact Made',
        icon: <Phone fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Contact Made`,
        counterLabel: 'contactMade',
      },
    ],
  },
  {
    label: 'Booked',
    icon: <AssignmentInd />,
    goToUrl: `${PAGE_URLS.JOBS}?status=Booked`,
    counterLabel: 'booked',
    subMenus: [
      {
        label: 'Collection Booked',
        icon: <CheckBox />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Collection Booked`,
        counterLabel: 'collectionBooked',
      },
      {
        label: 'Drop Off',
        icon: <TransferWithinAStation />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Drop Off`,
        counterLabel: 'dropOff',
      },
    ],
  },
  {
    label: 'Collected',
    icon: <NoCrash />,
    goToUrl: `${PAGE_URLS.JOBS}?status=Collected`,
    counterLabel: 'collected',
    subMenus: [
      {
        label: 'Paid',
        icon: <PhoneMissed fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Paid`,
        counterLabel: 'collectedPaid',
      },
      {
        label: 'Not Paid',
        icon: <Phone fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Not Paid`,
        counterLabel: 'collectedNotPaid',
      },
    ],
  },
  {
    label: 'Dropped Off ',
    icon: <Garage />,
    goToUrl: `${PAGE_URLS.JOBS}?status=Dropped Off`,
    counterLabel: 'droppedOff',
  },
  {
    label: 'Completed',
    icon: <FactCheck />,
    goToUrl: `${PAGE_URLS.JOBS}?status=Completed`,
    counterLabel: 'completed',
  },
  {
    label: 'Failed Collection',
    icon: <Error />,
    goToUrl: `${PAGE_URLS.JOBS}?status=Failed Collection`,
    counterLabel: 'failedCollection',
    subMenus: [
      {
        label: 'Incorrect Booking Details',
        icon: <AccountBalance fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Incorrect Booking Details`,
        counterLabel: 'cancelledIncorrectBookingDetails',
      },
      {
        label: 'No Access',
        icon: <CarCrash fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=No Access`,
        counterLabel: 'cancelledAccess',
      },
      {
        label: 'No Contact',
        icon: <PhoneDisabled fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=No Contact`,
        counterLabel: 'cancelledNoContact',
      },
      {
        label: 'Outside of Policy',
        icon: <Policy fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Outside of Policy`,
        counterLabel: 'cancelledOutsideOfPolicy',
      },
      {
        label: 'Price Dispute',
        icon: <Balance fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Price Dispute`,
        counterLabel: 'cancelledPriceDispute',
      },
      {
        label: 'Sold Elsewhere',
        icon: <CompareArrows fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Sold Elsewhere`,
        counterLabel: 'cancelledSold',
      },

      {
        label: 'Cancelled',
        icon: <Cancel fontSize="small" />,
        goToUrl: `${PAGE_URLS.JOBS}?status=Cancelled`,
        counterLabel: 'cancelledApproved',
      },
    ],
  },
]

export {
  loggedInRoutes,
  loggedInDriverRoutes,
  adminsRoutes,
  MENU_ITEMS,
  MENU_JOBS_STATUS,
}
